import type { MaybeRef } from 'vue'
import type { MaybeComputedElementRef } from '@vueuse/core'

export interface Ga4EcommerceItem {
  item_id: string
  item_name: string
  item_brand: string
  item_category?: string
  item_variant?: string
  price?: number
  quantity?: number
  promotion?: string
  index: number
  reference_value?: string
}

interface CarouselTrackingOptions<TPayload extends Record<string, unknown>> {
  currentSlide: MaybeRef<number>
  buildPayload: () => TPayload | undefined
  threshold?: number
}

/**
 * Generic carousel visibility tracker.
 * Fires a GTM event (with ecommerce clearing) each time a new slide
 * becomes visible in the viewport. Deduplicates per slide index.
 *
 * @example
 * // Custom event
 * useGtmVisibilityTracking(containerRef, {
 *   currentSlide,
 *   buildPayload: () => ({
 *     event: 'view_promotion',
 *     creative_name: toValue(creativeName),
 *     ecommerce: { items: toValue(promoItems) },
 *   }),
 * })
 */
export function useGtmVisibilityTracking<TPayload extends Record<string, unknown>>(
  target: MaybeComputedElementRef,
  options: CarouselTrackingOptions<TPayload>,
) {
  if (import.meta.server) return

  const gtm = useGtm()
  const isVisible = ref(false)
  const isReady = ref(false)
  const firedSlides = new Set<number>()

  const scheduleReady = typeof window !== 'undefined' && 'requestIdleCallback' in window
    ? (cb: () => void) => window.requestIdleCallback(cb)
    : (cb: () => void) => setTimeout(cb, 100)
  scheduleReady(() => {
    isReady.value = true
  })

  function fireViewItemList() {
    if (!isVisible.value || !isReady.value) return

    const slide = toValue(options.currentSlide)
    if (firedSlides.has(slide)) return

    const payload = options.buildPayload()
    if (!payload) return

    firedSlides.add(slide)

    // for reset ecommerce data
    // documentation: https://developers.google.com/tag-manager/web/ecom-ga4#reset_ecommerce_data
    gtm?.trackEvent({ ecommerce: null })
    gtm?.trackEvent(payload)
  }

  useIntersectionObserver(
    target,
    ([entry]) => {
      isVisible.value = !!entry?.isIntersecting
      if (isVisible.value) {
        fireViewItemList()
      }
    },
    { threshold: options.threshold ?? 0.5 },
  )

  watch([() => toValue(options.currentSlide), isReady], () => {
    fireViewItemList()
  })
}

interface ViewItemListOptions {
  itemListId: MaybeRef<string>
  itemListName: MaybeRef<string>
  items: MaybeRef<Ga4EcommerceItem[]>
  currentSlide: MaybeRef<number>
}

/**
 * Convenience wrapper: fires `view_item_list` with GA4 ecommerce items
 * when a carousel slide becomes visible.
 */
export function useViewItemListTracking(
  target: MaybeComputedElementRef,
  options: ViewItemListOptions,
) {
  useGtmVisibilityTracking(target, {
    currentSlide: options.currentSlide,
    buildPayload: () => {
      const items = toValue(options.items)
      if (!items.length) return undefined
      const itemListId = toValue(options.itemListId)
      const itemListName = toValue(options.itemListName)
      return {
        event: 'view_item_list' as const,
        item_list_id: itemListId,
        item_list_name: itemListName,
        ecommerce: {
          item_list_id: itemListId,
          item_list_name: itemListName,
          currency: 'EUR',
          items,
        },
      }
    },
  })
}
